// AdvancedExportModal - UPDATED WITH API INTEGRATION
// Phase 4 - Day 1: Advanced Export with Real API Calls
import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiDownload, FiCheck } from 'react-icons/fi';
import { theme } from '@theme';
import { exportTasksApi } from '@/services/api/notifications.api';

// Helper function to download a blob as a file
const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

interface AdvancedExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// [Styled components remain the same as before - copying key ones]

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.medium};
`;

const Modal = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.xl};
  width: 90%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.border};
  background: linear-gradient(135deg, ${theme.colors.primary}10 0%, ${theme.colors.secondary}10 100%);
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error}15;
    color: ${theme.colors.error};
  }

  svg {
    font-size: 24px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.xl};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${theme.spacing.md} 0;
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const FormatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${theme.spacing.md};
`;

const FormatButton = styled.button<{ selected: boolean }>`
  padding: ${theme.spacing.lg};
  border: 2px solid ${props => props.selected ? theme.colors.primary : theme.colors.border};
  background: ${props => props.selected ? `${theme.colors.primary}10` : 'white'};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-weight: ${theme.typography.weights.semibold};
  color: ${props => props.selected ? theme.colors.primary : theme.colors.text};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.md};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.background};
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const Input = styled.input`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.background};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.md};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary};
    color: white;
    &:hover { background: ${theme.colors.primaryDark}; }
  ` : `
    background: white;
    color: ${theme.colors.text};
    border: 2px solid ${theme.colors.border};
    &:hover { background: ${theme.colors.background}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 18px;
  }
`;

const ProgressContainer = styled.div`
  margin-top: ${theme.spacing.md};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${theme.colors.background};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
  width: ${props => props.progress}%;
  transition: width ${theme.transitions.medium};
`;

const ProgressText = styled.div`
  margin-top: ${theme.spacing.sm};
  text-align: center;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.textSecondary};
`;

export const AdvancedExportModal: React.FC<AdvancedExportModalProps> = ({ isOpen, onClose }) => {
  const [selectedFormat, setSelectedFormat] = useState<'excel' | 'csv' | 'pdf' | 'json'>('excel');
  const [selectedFields, setSelectedFields] = useState<string[]>([
    'candidateName',
    'status',
    'score',
    'agentName',
    'submittedDate',
  ]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const availableFields = [
    { id: 'candidateName', label: 'Nom du candidat' },
    { id: 'position', label: 'Poste' },
    { id: 'status', label: 'Statut' },
    { id: 'score', label: 'Score IA' },
    { id: 'agentName', label: 'Agent' },
    { id: 'submittedDate', label: 'Date de soumission' },
    { id: 'priority', label: 'Priorité' },
    { id: 'validatedDate', label: 'Date de validation' },
  ];

  const handleFieldToggle = (fieldId: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      alert('Veuillez sélectionner au moins un champ à exporter');
      return;
    }

    try {
      setIsExporting(true);
      setProgress(0);

      // Create export task via API
      const exportTask = await exportTasksApi.create({
        format: selectedFormat,
        config: {
          fields: selectedFields,
          filters: {
            status: statusFilter.length > 0 ? statusFilter : undefined,
            priority: priorityFilter.length > 0 ? priorityFilter : undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
        },
      });

      // Poll for progress and auto-download when complete
      const blob = await exportTasksApi.pollAndDownload(
        exportTask.id,
        (progressData) => {
          setProgress(progressData.percentage);
        },
        1000 // Poll every second
      );

      // Download the file
      const extension = selectedFormat;
      const filename = `export_${new Date().toISOString().split('T')[0]}.${extension}`;
      downloadFile(blob, filename);

      // Success
      setIsExporting(false);
      setProgress(0);
      onClose();

      // Show success message (you can replace with a toast)
      alert('Export terminé avec succès!');

    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
      setProgress(0);
      alert(`Erreur lors de l'export: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Export Avancé</Title>
          <CloseButton onClick={handleClose} disabled={isExporting}>
            <FiX />
          </CloseButton>
        </Header>

        <Content>
          {/* Format Selection */}
          <Section>
            <SectionTitle>Format d'export</SectionTitle>
            <FormatGrid>
              <FormatButton
                selected={selectedFormat === 'excel'}
                onClick={() => setSelectedFormat('excel')}
                disabled={isExporting}
              >
                Excel (.xlsx)
              </FormatButton>
              <FormatButton
                selected={selectedFormat === 'csv'}
                onClick={() => setSelectedFormat('csv')}
                disabled={isExporting}
              >
                CSV
              </FormatButton>
              <FormatButton
                selected={selectedFormat === 'pdf'}
                onClick={() => setSelectedFormat('pdf')}
                disabled={isExporting}
              >
                PDF
              </FormatButton>
              <FormatButton
                selected={selectedFormat === 'json'}
                onClick={() => setSelectedFormat('json')}
                disabled={isExporting}
              >
                JSON
              </FormatButton>
            </FormatGrid>
          </Section>

          {/* Fields Selection */}
          <Section>
            <SectionTitle>Champs à exporter</SectionTitle>
            <CheckboxGrid>
              {availableFields.map(field => (
                <CheckboxLabel key={field.id}>
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field.id)}
                    onChange={() => handleFieldToggle(field.id)}
                    disabled={isExporting}
                  />
                  {field.label}
                </CheckboxLabel>
              ))}
            </CheckboxGrid>
          </Section>

          {/* Date Range Filter */}
          <Section>
            <SectionTitle>Plage de dates</SectionTitle>
            <FilterRow>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                disabled={isExporting}
                placeholder="Date de début"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                disabled={isExporting}
                placeholder="Date de fin"
              />
            </FilterRow>
          </Section>

          {/* Progress Bar */}
          {isExporting && (
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill progress={progress} />
              </ProgressBar>
              <ProgressText>
                Export en cours... {Math.round(progress)}%
              </ProgressText>
            </ProgressContainer>
          )}
        </Content>

        <Footer>
          <Button onClick={handleClose} disabled={isExporting}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <>Exportation...</>
            ) : (
              <>
                <FiDownload />
                Exporter
              </>
            )}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};
